import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, ScaleControl, useMap } from 'react-leaflet';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
// アプリケーションのメインファイル（index.jsやApp.jsなど）でLeafletのCSSをインポートすることを忘れないでください。
// 例: import 'leaflet/dist/leaflet.css';

// マップの動作を制御するためのコンポーネント
function MapController() {
  const map = useMap();
  // このコンポーネントがマウントされた時に一度だけ実行
  React.useEffect(() => {
    map.scrollWheelZoom.disable(); // マウスホイールによるズームを無効化
  }, [map]);
  return null;
}

// 初期表示に戻るボタンコンポーネント
function ResetViewControl({ center, zoom }) {
  const map = useMap();

  const onResetClick = () => {
    map.setView(center, zoom);
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000, // Ensure it's on top of map layers and controls
      }}
    >
      <IconButton
        onClick={onResetClick}
        aria-label="reset view"
        title="Reset view"
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          '&:hover': { backgroundColor: 'white' },
        }}
      >
        <ReplayIcon />
      </IconButton>
    </Box>
  );
}

export default function GbifOccurrenceMap({ taxonKeys = [] }) {
  console.log('[GbifOccurrenceMap] Rendering with taxonKeys:', taxonKeys);
  const [occurrenceCount, setOccurrenceCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);

  useEffect(() => {
    if (!taxonKeys || taxonKeys.length === 0) {
      setOccurrenceCount(null);
      return;
    }

    const fetchOccurrenceCount = async () => {
      setLoadingCount(true);
      try {
        const counts = await Promise.all(
          taxonKeys.map(key =>
            fetch(`https://api.gbif.org/v1/occurrence/search?limit=0&hasGeospatialIssue=false&hasCoordinate=true&taxonKey=${key}`)
              .then(res => {
                if (!res.ok) throw new Error(`GBIF API request failed for key ${key} with status ${res.status}`);
                return res.json();
              })
              .then(data => data.count || 0)
          )
        );
        const totalCount = counts.reduce((sum, count) => sum + count, 0);
        setOccurrenceCount(totalCount);
      } catch (error) {
        console.error("Failed to fetch GBIF occurrence count:", error);
        setOccurrenceCount(null);
      } finally {
        setLoadingCount(false);
      }
    };

    fetchOccurrenceCount();
  }, [taxonKeys]);

  if (!taxonKeys || taxonKeys.length === 0) {
    console.log('[GbifOccurrenceMap] No taxonKeys provided, rendering null.');
    // Hooks are called unconditionally before this, so we can safely return early.
    return null;
  }

  const taxonKeysString = taxonKeys.join(',');
  // 複数キーのときはタイルレイヤーを個別に重ねる（複数taxonKeyの同時指定で503になるケースを回避）
  const occurrenceLayerUrls = taxonKeys.map(
    (id) => `https://api.gbif.org/v2/map/occurrence/density/{z}/{x}/{y}@1x.png?srs=EPSG:3857&taxonKey=${id}&style=classic.poly&bin=hex&hexPerTile=70`
  );
  console.log('[GbifOccurrenceMap] Occurrence Layer URLs:', occurrenceLayerUrls);

  // GBIF推奨のベースマップ（中間的な明るさのダークモード）
  const baseLayerUrl = "https://tile.gbif.org/3857/omt/{z}/{x}/{y}@1x.png?style=gbif-middle";

  // 地図の表示範囲を制限
  const bounds = [
    [-90, -180], // South-West
    [90, 180]    // North-East
  ];

  const initialCenter = [20, 0];
  const initialZoom = 2;

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 1, height: 24, display: 'flex', alignItems: 'center' }}>
        {loadingCount ? (
          <CircularProgress size={20} />
        ) : occurrenceCount !== null ? (
          <Typography variant="body2" color="text.secondary" title="Georeferenced occurrences">
            Georeferenced: {occurrenceCount.toLocaleString()}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ flexGrow: 1, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.800', minHeight: 0 }}>
        {/* Add key to force re-render when taxonKeys change, which is a common fix for Leaflet in React */}
        <MapContainer key={taxonKeysString} center={initialCenter} zoom={initialZoom} minZoom={initialZoom} style={{ height: '100%', width: '100%' }} maxBounds={bounds} maxBoundsViscosity={1.0}>
          <MapController />
          <ResetViewControl center={initialCenter} zoom={initialZoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.gbif.org">GBIF</a>'
            url={baseLayerUrl}
            noWrap={true}
          />
          {occurrenceLayerUrls.map((url, idx) => (
            <TileLayer key={`${url}-${idx}`} url={url} zIndex={2} noWrap={true} opacity={occurrenceLayerUrls.length > 1 ? 0.7 : 1} />
          ))}
          <ScaleControl position="bottomleft" />
        </MapContainer>
      </Box>
    </Box>
  );
}
